import { Suspense } from "react";

import { defineMessages, useIntl } from "react-intl";

import {
    PriceFinderMessages,
    ProductPlan
} from "@/components/wpl/pricing/src/price-finder/constants";
import PriceFinderSection from "@/components/wpl/pricing/src/price-finder/price-finder";

import { ConfluencePricingPageProps } from "../../../../pages/software/confluence/pricing/confluence-pricing-page";
import ActiveWhiteboards from "../expander-contents/active-whiteboards";
import AdminControlsInsights from "../expander-contents/admin-controls-insights";
import AdvancedAdminControls from "../expander-contents/advanced-admin-controls";
import AtlassianIntelligence from "../expander-contents/atlassian-intelligence";
import PerUserAutomation from "../expander-contents/automation-per-user";
import CrossProductInsights from "../expander-contents/cross-product-insights";
import EnterpiseSupport from "../expander-contents/enterprise-support";
import ExternalCollaboration from "../expander-contents/external-collaboration";
import IdentityAccessManagement from "../expander-contents/identity-access-management";
import MultipleInstances from "../expander-contents/multiple-instances";
import PagePermissions from "../expander-contents/page-permissions";
import PremiumSupport from "../expander-contents/premium-support";
import RegionalSupport from "../expander-contents/regional-support";
import SupportAppsIntegrations from "../expander-contents/support-apps-integrations";
import SupportAtlassianCommunity from "../expander-contents/support-atlassian-community";
import Templates from "../expander-contents/templates";
import UnlimitedAutomations from "../expander-contents/unlimited-automations";
import UnlimitedPages from "../expander-contents/unlimited-pages";
import UnlimitedSpaces from "../expander-contents/unlimited-spaces";
import UnlimitedUsers from "../expander-contents/unlimited-users";
import UnlimitedWhiteboards from "../expander-contents/unlimited-whiteboards";

const ConfluencePriceFinderTeam24 = (props: ConfluencePricingPageProps) => {
    const intl = useIntl();

    const newTagText = intl.formatMessage({
        id: "ConfluencePricingPage.priceFinder.newTagText",
        defaultMessage: "New"
    });

    const freePlanFeatureMessages = [
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.unlimitedPages.title",
                    defaultMessage: "Unlimited pages"
                }),
                content: UnlimitedPages
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.unlimitedSpaces.title",
                    defaultMessage: "Unlimited spaces"
                }),
                content: UnlimitedSpaces
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.templates.title",
                    defaultMessage:
                        "Templates for Marketing, Product, Program Management and more"
                }),
                content: Templates
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.activeWhiteboards.title",
                    defaultMessage: "Up to 3 active whiteboards per user "
                }),
                content: ActiveWhiteboards
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.supportAppsAndIntegrations",
                    defaultMessage: "Support for apps and integrations"
                }),
                content: SupportAppsIntegrations
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.supportFromAtlassianCommunity",
                    defaultMessage: "Support from Atlassian Community"
                }),
                content: SupportAtlassianCommunity
            }
        },
        {
            featureMessage: intl.formatMessage({
                id: "ConfluencePricingPageTeam24.features.freeFileStorage",
                defaultMessage: "Up to 2 GB file storage"
            })
        }
    ];

    const standardPlanFeatureMessages = [
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.externalCollaboration.title",
                    defaultMessage: "External collaboration"
                }),
                content: ExternalCollaboration
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.pagePermissions.title",
                    defaultMessage: "Page permissions"
                }),
                content: PagePermissions
            }
        },
        {
            featureMessage: intl.formatMessage({
                id: "ConfluencePricingPageTeam24.features.standardStorage.title",
                defaultMessage: "250 GB of storage"
            })
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.regionalSupport.title",
                    defaultMessage: "9/5 regional support"
                }),
                content: RegionalSupport
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.standardUnlimitedUsers.title",
                    defaultMessage: "Unlimited users"
                }),
                content: UnlimitedUsers
            }
        }
    ];

    const premiumPlanFeatureMessages = [
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.aiPowered.title",
                    defaultMessage: "AI-powered creation and collaboration"
                }),
                content: AtlassianIntelligence
            },
            lozengeText: newTagText
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.perUserAutomation.title",
                    defaultMessage:
                        "Per user automation limits (1000 per month)"
                }),
                content: PerUserAutomation
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.unlimitedWhiteboards.title",
                    defaultMessage: "Unlimited whiteboards"
                }),
                content: UnlimitedWhiteboards
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.adminControlsInsights.title",
                    defaultMessage: "Admin controls and insights"
                }),
                content: AdminControlsInsights
            }
        },
        {
            featureMessage: intl.formatMessage({
                id: "ConfluencePricingPageTeam24.features.unlimitedStorage.title",
                defaultMessage: "Unlimited storage"
            })
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.premiumSupport.title",
                    defaultMessage: "24/7 support for critical issues"
                }),
                content: PremiumSupport
            }
        },
        {
            featureMessage: intl.formatMessage({
                id: "ConfluencePricingPageTeam24.features.premiumSla.title",
                defaultMessage: "99.9% uptime SLA"
            })
        }
    ];

    const enterprisePlanFeatureMessages = [
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.crossProductInsights.title",
                    defaultMessage:
                        "Cross-product insights with Atlassian Analytics and Data Lake"
                }),
                content: CrossProductInsights
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.advancedAdmin.title",
                    defaultMessage: "Advanced admin controls and security"
                }),
                content: AdvancedAdminControls
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.enterpriseIAM.title",
                    defaultMessage:
                        "Enterprise-grade identity and access management"
                }),
                content: IdentityAccessManagement
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.enterpriseAutomations.title",
                    defaultMessage: "Unlimited automations"
                }),
                content: UnlimitedAutomations
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.multipleInstances.title",
                    defaultMessage: "Multiple instances (up to 150)"
                }),
                content: MultipleInstances
            }
        },
        {
            featureMessage: {
                text: intl.formatMessage({
                    id: "ConfluencePricingPageTeam24.features.enterpriseSupport.title",
                    defaultMessage: "24/7 support for all issues"
                }),
                content: EnterpiseSupport
            }
        },
        {
            featureMessage: intl.formatMessage({
                id: "ConfluencePricingPageTeam24.features.enterpriseSLA.title",
                defaultMessage: "99.95% uptime SLA"
            })
        }
    ];

    // planNames should not be translated
    const confluenceProductPlans: ProductPlan<"confluence">[] = [
        {
            planName: "Free",
            offeringSku: "confluence-free",
            description: intl.formatMessage({
                id: "ConfluencePricingPageTeam24.priceFinder.freeDescription",
                defaultMessage: "Free forever for 10 users"
            }),
            recommendationLimit: 0,
            signupLimit: 50000,
            signupURLSuffix:
                "?bundle=confluence&edition=free&signupSource=pricing",
            planFeatureMessages: freePlanFeatureMessages
        },
        {
            planName: "Standard",
            offeringSku: "confluence-standard",
            description: intl.formatMessage({
                id: "ConfluencePricingPageTeam24.priceFinder.standardDescription",
                defaultMessage: "Everything you need to get started"
            }),
            recommendationLimit: 10,
            signupLimit: 50000,
            signupURLSuffix: "?bundle=confluence&edition=standard",
            planFeatureMessages: standardPlanFeatureMessages
        },
        {
            planName: "Premium",
            offeringSku: "confluence-premium",
            description: intl.formatMessage({
                id: "ConfluencePricingPageTeam24.priceFinder.premiumDescription",
                defaultMessage: "Connect work across teams"
            }),
            recommendationLimit: 250,
            signupLimit: 50000,
            signupURLSuffix: "?bundle=confluence&edition=premium",
            planFeatureMessages: premiumPlanFeatureMessages
        },
        {
            planName: "Enterprise",
            description: intl.formatMessage({
                id: "ConfluencePricingPageTeam24.priceFinder.enterpriseDescription",
                defaultMessage:
                    "Advanced analytics, scale and security for enterprises"
            }),
            recommendationLimit: 5000,
            signupLimit: 50000,
            planFeatureMessages: enterprisePlanFeatureMessages
        }
    ];

    const messages: PriceFinderMessages = defineMessages({
        headerText: {
            id: "ConfluencePricingPageTeam24.priceFinder.headerText",
            defaultMessage: "Build Your <blue>Source of Truth</blue>"
        },
        unitCountInputTextRedesign: {
            id: "ConfluencePricingPage.priceFinder.unitCountInputTextRedesign",
            defaultMessage: "Team size:"
        },
        unitCountInputTerm: {
            id: "ConfluencePricingPage.priceFinder.unitCountInputTerm",
            defaultMessage: "users"
        },
        billingCycleText: {
            id: "ConfluencePricingPage.priceFinder.billingCycleText",
            defaultMessage: "Billing cycle:"
        },
        unitCountInputText: {
            id: "ConfluencePricingPage.priceFinder.unitCountInputText",
            defaultMessage: "How many users are on your team?"
        },
        unitCountInputTooltip: {
            id: "ConfluencePricingPage.priceFinder.unitCountInputTooltip",
            defaultMessage:
                "A Confluence user is any user who can log in to Confluence, and who exists in User Management"
        },
        monthlyExampleHelpText: {
            id: "ConfluencePricingPage.priceFinder.monthlyExampleHelpText",
            defaultMessage:
                "Monthly plans give you flexibility as your number of active users changes, so pay for seats as you use them. Remember that the number of seats may change the total price."
        },
        annualExampleHelpText: {
            id: "ConfluencePricingPage.priceFinder.annualExampleHelpText",
            defaultMessage:
                "Annual plans give you flexibility and help you prepare for growth. Buy a group of seats at a flat rate now, then fill your empty seats through the year at no extra cost."
        },
        exampleStandardPlan: {
            id: "ConfluencePricingPage.priceFinder.exampleStandardPlan",
            defaultMessage: "Pricing for {unitCount} users on Standard plan",
            description: "Standard is a plan name and should not be translated"
        },
        monthlyExampleUnitCountText: {
            id: "ConfluencePricingPage.priceFinder.monthlyExampleUserCount",
            defaultMessage: "User count"
        },
        monthlyExampleUnitCountAt: {
            id: "ConfluencePricingPage.priceFinder.monthlyExampleUnitCountAt",
            defaultMessage: "users @",
            description: "will be used as follows: 100 users @ $8.15"
        },
        monthlyExampleUnitCountTotal: {
            id: "ConfluencePricingPage.priceFinder.monthlyExampleUnitCount",
            defaultMessage: "{unitCount} users"
        },
        monthlyExampleEstimatedPrice: {
            id: "ConfluencePricingPage.priceFinder.monthlyExampleEstimatedPrice",
            defaultMessage: "Estimated price per user: <strong>{price}</strong>"
        },
        annualExampleUnitCountTierText: {
            id: "ConfluencePricingPage.priceFinder.annualExampleUnitCountTierText",
            defaultMessage: "User tier"
        },
        annualExampleEstimatedPrice: {
            id: "ConfluencePricingPage.priceFinder.annualExampleEstimatedPrice",
            defaultMessage:
                "Estimated price per user/per month: <strong>{price}</strong>"
        },
        cardOverSignupLimit: {
            id: "ConfluencePricingPage.priceFinder.cardOverSignupLimit",
            defaultMessage: "Your user count exceeds this plan's limit"
        },
        cardEnterpriseMinimumCount: {
            id: "ConfluencePricingPage.priceFinder.cardEnterpriseMinimumCount",
            defaultMessage:
                "Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more users above",
            description:
                "Enterprise is a plan name and should not be translated"
        },
        cardUnitCountTierRange: {
            id: "ConfluencePricingPage.priceFinder.cardUnitCountTierRange",
            defaultMessage: "User tier: {lowerLimit} - {upperLimit}"
        },
        cardMonthlyPerUnitCount: {
            id: "ConfluencePricingPageTeam24.priceFinder.cardMonthlyPerUnitCount",
            defaultMessage: "per user / month"
        },
        cardFreeDescription: {
            id: "ConfluencePricingPage.priceFinder.cardFreeDescription",
            defaultMessage: "Always free for 10 users"
        },
        freeLimitModalTitle: {
            id: "ConfluencePricingPage.priceFinder.freeLimitModalTitle",
            defaultMessage: "Free has a {freeLimitCount}-user limit",
            description: "Free is a plan name and should not be translated"
        },
        freeLimitModalMessage: {
            id: "ConfluencePricingPage.priceFinder.freeLimitModalMessage",
            defaultMessage:
                "If you'd like to add more than {freeLimitCount} users, consider another plan instead."
        },
        discountLozengeText: {
            id: "ConfluencePricingPage.priceFinder.discountLozengeText",
            defaultMessage: "SAVE UP TO 17%"
        }
    });

    return (
        <Suspense>
            <PriceFinderSection
                productPlans={confluenceProductPlans}
                hamsProductKey="pricingplan.confluence.ondemand"
                messages={messages}
                serverSideCCPPricing={props.pricing?.ccpPricingData?.confluence}
                serverSideHAMSPricing={
                    props.pricing?.hamsPricingData?.confluence
                }
                countryCode={props.countryCode}
                dataProductKey="confluence"
                defaultUnitCount={300}
            />
        </Suspense>
    );
};

export default ConfluencePriceFinderTeam24;
